import { type ClassValue, clsx } from "clsx";

/**
 * Utility function to merge class names
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Format cents to ZAR currency string
 * @param cents - Amount in cents
 * @returns Formatted string like "R 1,234.56"
 */
export function formatCurrency(cents: number): string {
  const amount = cents / 100;
  return `R ${amount.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format date to readable string
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Get session token from localStorage
 */
export function getSessionToken(): string | null {
  return localStorage.getItem("session_token");
}

/**
 * Set session token in localStorage
 */
export function setSessionToken(token: string): void {
  localStorage.setItem("session_token", token);
}

/**
 * Get session ID from localStorage
 */
export function getSessionId(): string | null {
  return localStorage.getItem("session_id");
}

/**
 * Set session ID in localStorage
 */
export function setSessionId(sessionId: string): void {
  localStorage.setItem("session_id", sessionId);
}

/**
 * Clear session data from localStorage
 */
export function clearSession(): void {
  localStorage.removeItem("session_token");
  localStorage.removeItem("session_id");
}

/**
 * Extract session token and session_id from URL params
 * Checks both query params and path params
 */
export function extractSessionFromUrl(): { token: string | null; sessionId: string | null } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("session") || params.get("session_token");
  const sessionId = params.get("session_id");

  // Also check path for sessionId (e.g., /order/:sessionId)
  const pathMatch = window.location.pathname.match(/\/order\/([^/]+)/);
  const pathSessionId = pathMatch ? pathMatch[1] : null;

  const finalSessionId = sessionId || pathSessionId;

  // Return if we have at least one value
  if (token || finalSessionId) {
    return { token: token || null, sessionId: finalSessionId || null };
  }

  return null;
}

/**
 * Validate South African phone number
 */
export function validateSAPhone(phone: string): boolean {
  // Match +27 or 0 followed by 8-9 digits
  const saPhoneRegex = /^(\+27|0)[6-8]\d{8}$/;
  return saPhoneRegex.test(phone.replace(/\s/g, ""));
}

/**
 * Format phone number to E.164 format for South Africa
 */
export function formatPhoneToE164(phone: string): string {
  const cleaned = phone.replace(/\s/g, "");

  if (cleaned.startsWith("+27")) {
    return cleaned;
  }

  if (cleaned.startsWith("0")) {
    return `+27${cleaned.slice(1)}`;
  }

  return `+27${cleaned}`;
}

/**
 * Validate South African postal code
 */
export function validateSAPostalCode(postalCode: string): boolean {
  // South African postal codes are 4 digits
  const postalCodeRegex = /^\d{4}$/;
  return postalCodeRegex.test(postalCode);
}

/**
 * South African provinces
 */
export const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
] as const;

export type SAProvince = (typeof SA_PROVINCES)[number];
